import { cn } from "@istari-frontend/shared";
import { useEffect, useRef, useState } from "react";
import { Virtuoso, VirtuosoHandle } from "react-virtuoso";

import { useCodeLines } from "~/components/use-code-lines";

import { SelectionAction } from "./file-viewer/selection-action";
import { ViewerPrefs } from "./file-viewer/url-fragment";

interface CodeViewerProps {
  text: string;
  versionId?: string;
  initialView?: ViewerPrefs;
  onSelectionChange?: (action: SelectionAction) => void;
}

export function CodeViewer({ text, versionId, initialView, onSelectionChange }: CodeViewerProps) {
  const virtuosoRef = useRef<VirtuosoHandle>(null);
  const [selection, setSelection] = useState<{
    start: number;
    end: number;
  } | null>(
    initialView?.versionId === versionId && initialView?.codeSelection
      ? {
          start: Math.max(1, Math.min(initialView.codeSelection.startLine, initialView.codeSelection.endLine)),
          end: Math.max(initialView.codeSelection.startLine, initialView.codeSelection.endLine),
        }
      : null,
  );
  const anchorRef = useRef<number | null>(null);
  const appliedInitialScrollRef = useRef(false);

  const { getLine, count, digits } = useCodeLines(text);

  useEffect(() => {
    if (appliedInitialScrollRef.current) return;
    if (!selection) return;
    appliedInitialScrollRef.current = true;
    const startLine = selection.start;
    virtuosoRef.current?.scrollToIndex({ index: startLine - 1 });
  }, [selection]);

  const commit = (next: { start: number; end: number } | null) => {
    if (!onSelectionChange) return;

    if (next) {
      const startLine = Math.max(1, Math.min(next.start, next.end));
      const endLine = Math.min(count, Math.max(next.start, next.end));
      onSelectionChange({ type: "line-range", startLine, endLine });
    } else {
      onSelectionChange({ type: "clear" });
    }
  };

  const begin = (line: number, withShift: boolean) => {
    if (withShift && anchorRef.current !== null) {
      const next = { start: anchorRef.current, end: line };
      setSelection(next);
      commit(next);
    } else {
      anchorRef.current = line;
      const next = { start: line, end: line };
      setSelection(next);
      commit(next);
    }
  };

  return (
    <div className="bg-background h-full w-full overflow-x-hidden py-2 font-mono text-xs">
      <div className="h-full min-w-full">
        <Virtuoso
          ref={virtuosoRef}
          totalCount={count}
          itemContent={(idx) => {
            const lineText = getLine(idx);
            const lineNumber = idx + 1;
            const isSelected = selection
              ? lineNumber >= Math.min(selection.start, selection.end) &&
                lineNumber <= Math.max(selection.start, selection.end)
              : false;
            const isStart = selection ? lineNumber === Math.min(selection.start, selection.end) : false;
            const isEnd = selection ? lineNumber === Math.max(selection.start, selection.end) : false;
            return (
              <div
                key={idx}
                className={cn(
                  "relative grid w-full leading-5",
                  isSelected ? "bg-primary/5" : "",
                  isSelected && isStart
                    ? "before:bg-primary before:absolute before:top-0 before:right-0 before:left-0 before:z-20 before:h-px"
                    : "",
                  isSelected && isEnd
                    ? "after:bg-primary after:absolute after:right-0 after:bottom-0 after:left-0 after:z-20 after:h-px"
                    : "",
                )}
                style={{ gridTemplateColumns: `${digits + 2}ch 1fr` }}
              >
                <div
                  className={cn(
                    "border-border bg-card text-muted-foreground sticky left-0 z-10 pr-3 text-right select-none",
                    "hover:bg-muted hover:text-foreground cursor-default",
                  )}
                  style={{ width: `${digits + 2}ch` }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    begin(lineNumber, e.shiftKey);
                  }}
                >
                  {lineNumber}
                </div>
                <div className="w-full min-w-0 pr-1.5 pl-1.5 break-words whitespace-pre-wrap">
                  {lineText.length ? lineText : "\u00A0"}
                </div>
              </div>
            );
          }}
          defaultItemHeight={18}
          increaseViewportBy={180}
        />
      </div>
    </div>
  );
}
